import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { GOOGLE_PLACE } from "@/lib/config";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [orderNumber, setOrderNumber] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrderNumber(data.order_number);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (err) {
        console.warn("Payment status poll failed, retrying", err?.message);
      }
      attempts.current += 1;
      if (attempts.current >= 10) {
        setState("timeout");
      } else {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="payment-success-page">
      <Navbar />
      <main className="pt-32 pb-20 max-w-xl mx-auto px-4 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="h-14 w-14 text-cyan-500 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold text-slate-900">Verificando tu pago...</h1>
            <p className="text-slate-600 mt-2">Un momento, estamos confirmando la transacción.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-heading font-bold text-slate-900">¡Pago confirmado!</h1>
            <p className="text-slate-600 mt-3">
              Tu pedido <strong data-testid="payment-order-number">{orderNumber}</strong> está confirmado.
              Te hemos enviado un correo con todos los detalles. Pasaremos a recoger tu ropa en la fecha indicada.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
              <Button asChild className="bg-cyan-500 hover:bg-cyan-600 rounded-full px-8 h-11" data-testid="payment-home-btn">
                <Link to="/">Volver al inicio</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-8 h-11" data-testid="payment-track-btn">
                <Link to={`/seguimiento?pedido=${orderNumber || ""}`}>Seguir mi pedido</Link>
              </Button>
            </div>
            <p className="mt-8 text-sm text-slate-500" data-testid="payment-google-review-hint">
              ¿Ya nos conoces?{" "}
              <a href={GOOGLE_PLACE.reviewUrl} target="_blank" rel="noopener noreferrer" data-testid="payment-google-review-link"
                className="text-cyan-700 underline underline-offset-2 hover:text-cyan-800">
                Déjanos una reseña en Google
              </a>{" "}
              y ayuda a otros vecinos a encontrarnos.
            </p>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-error">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              {state === "timeout" ? "La verificación tarda más de lo esperado" : "No pudimos confirmar el pago"}
            </h1>
            <p className="text-slate-600 mt-3">
              {state === "timeout"
                ? "Si el cargo se realizó, recibirás el correo de confirmación en breve."
                : "Inténtalo de nuevo o contáctanos por WhatsApp."}
            </p>
            <Button asChild variant="outline" className="mt-8 rounded-full px-8">
              <Link to="/reservar">Volver a reservar</Link>
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
